import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  Validators,
  FormsModule,
  ReactiveFormsModule,
  FormControl,
  FormGroup,
  FormBuilder,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { EnvironmentService } from 'src/app/core/environment.service';
import { SetUserPasswordRequestBody } from '@app/core/models/register-service';
import { RegisterService } from '@app/core/services/register.service';
import { TrackingService } from '@app/core/services/tracking/tracking.service';
import { CustomValidators } from '@app/shared/validators';
import { LocaleService } from '@app/core/services';
import { TranslateModule } from '@ngx-translate/core';
import { RCFieldModule, RCButtonModule } from '@rc/ui';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { ControlsOf } from '@app/shared/utils/form-utils';

@Component({
  selector: 'app-set-password-page',
  templateUrl: './set-password-page.component.html',
  styleUrls: ['./set-password-page.component.scss'],
  imports: [
    LoaderComponent,
    FormsModule,
    ReactiveFormsModule,
    RCFieldModule,
    TranslateModule,
    RCButtonModule,
  ],
})
export class SetPasswordPageComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private registerService = inject(RegisterService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private localeService = inject(LocaleService);
  private trackingService = inject(TrackingService);
  private environmentService = inject(EnvironmentService);

  showPassword = false;
  showPasswordConfirm = false;
  showPopover = false;
  setPasswordForm!: FormGroup<
    ControlsOf<{ password: string; passwordConfirm: string }>
  >;
  submitted = false;
  // Validation message in case of error in form
  validationMessages!: {
    password: { type: string; message: string }[];
    passwordConfirm: { type: string; message: string }[];
  };
  passwordError?: string;

  private translateRulesKey = [
    'fieldRules_required',
    'setPassword_rules1',
    'setPassword_rules2',
    'setPassword_rules3',
    'setPassword_rules4',
    'setPassword_rules5',
    'fieldRules_same',
  ];
  private _destroyed$ = new Subject<void>();
  private _callback?: string;
  private _token?: string;

  constructor() {
    // Get form rules translation
    this.localeService
      .translateGet(this.translateRulesKey)
      .subscribe((translations) => {
        if (translations) {
          this.validationMessages = {
            password: [
              { type: 'required', message: translations.fieldRules_required },
              { type: 'hasNumber', message: translations.setPassword_rules4 },
              {
                type: 'hasCapitalCase',
                message: translations.setPassword_rules3,
              },
              {
                type: 'hasSmallCase',
                message: translations.setPassword_rules2,
              },
              {
                type: 'hasSpecialCharacters',
                message: translations.setPassword_rules5,
              },
              { type: 'minlength', message: translations.setPassword_rules1 },
            ],
            passwordConfirm: [
              { type: 'required', message: translations.fieldRules_required },
              {
                type: 'NoPassswordMatch',
                message: translations.fieldRules_same,
              },
            ],
          };
        }
      });
  }

  ngOnInit() {
    this.localeService.setTitle('setPassword_title');
    this.route.fragment.subscribe((fragment) => {
      if (fragment) {
        try {
          const decoded = JSON.parse(atob(fragment));
          this._callback = decoded.callback;
          this._token = decoded.token;
        } catch {}
        if (!this._callback || !this._token) {
          this.router.navigate(['/error']);
        }
      } else {
        this.notAuthorized();
      }
    });

    // Build form
    this.buildSetPasswordForm();
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  popoverOpen() {
    this.showPopover = true;
  }
  popoverClose() {
    this.showPopover = false;
  }

  onSubmit() {
    if (this.submitted && this.setPasswordForm.invalid) {
      return;
    }
    this.submitted = true;

    // Send data
    this.sendData(this.setPasswordForm.controls.password.value!);
  }

  get passwordControl() {
    return this.setPasswordForm.get('password');
  }

  get passwordConfirmControl() {
    return this.setPasswordForm.get('passwordConfirm');
  }

  private notAuthorized() {
    window.location.href = this.environmentService.getGlobalRedirectURI();
  }

  private buildSetPasswordForm() {
    this.setPasswordForm = this.formBuilder.group(
      {
        password: new FormControl<string | null>(null, [
          Validators.required,
          // check whether the entered password has a number
          CustomValidators.patternValidator(/\d/, { hasNumber: true }, true),
          // check whether the entered password has upper case letter
          CustomValidators.patternValidator(
            /[A-Z]/,
            { hasCapitalCase: true },
            true,
          ),
          // check whether the entered password has a lower-case letter
          CustomValidators.patternValidator(
            /[a-z]/,
            { hasSmallCase: true },
            true,
          ),
          // check whether the entered password has a special character
          CustomValidators.patternValidator(
            /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
            { hasSpecialCharacters: true },
            true,
          ),
          // Has a minimum length of 8 characters
          Validators.minLength(8),
        ]),
        passwordConfirm: new FormControl<string | null>(
          null,
          Validators.required,
        ),
      },
      {
        // Check whether our password and confirm password match
        validators: CustomValidators.passwordMatchValidator,
      },
    );
    this.setPasswordForm.controls.password.valueChanges
      .pipe(
        takeUntil(this._destroyed$),
        tap(() =>
          this.setPasswordForm.controls.passwordConfirm.updateValueAndValidity(),
        ),
      )
      .subscribe();
  }

  private sendData(password: string) {
    const body: SetUserPasswordRequestBody = { token: this._token!, password };
    this.registerService.setUserPassword(body).subscribe({
      next: (response) => {
        try {
          const { sessionToken } = JSON.parse(response);

          const url = new URL(this._callback!);
          const search = url.search ? url.search.substring(1).split('&') : [];
          search.push(`token=${sessionToken}`);
          url.search = `?${search.join('&')}`;

          this._callback = url.href;
        } catch (e) {}
        this.trackingService.pushEvent('resetPassword.success');
        this.router.navigate(['/reset/success'], {
          state: { data: this._callback },
        });
      },
      error: (error) => {
        this.trackingService.pushEvent('resetPassword.error');
        if (error.status === 403) {
          this.passwordError = body.password;
          this.submitted = false;
        } else {
          this.router.navigate(['/reset/failure'], {
            state: { data: this._callback },
          });
        }
      },
    });
  }
}
